import { useEffect, useState, type CSSProperties } from 'react';

interface SplashScreenProps {
  onRegistrar: () => void;
  onEntrar: () => void;
  onSetupBiometrics: () => void;
  onBiometricLogin: () => void;
  biometricsEnabled: boolean;
}

const quickLinks = [
  { icon: 'ri-user-add-line', label: 'Registrarme' },
  { icon: 'ri-team-line', label: 'Familia' },
  { icon: 'ri-bank-card-line', label: 'Pagos' },
  { icon: 'ri-shield-line', label: 'Tu plan' },
];

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buenos dias';
  if (hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

export default function SplashScreen({
  onRegistrar,
  onEntrar,
  onSetupBiometrics: _onSetupBiometrics,
  onBiometricLogin: _onBiometricLogin,
  biometricsEnabled: _biometricsEnabled,
}: SplashScreenProps) {
  const greeting = getGreeting();
  const [showBrandIntro, setShowBrandIntro] = useState(true);
  const [showContigoDetails, setShowContigoDetails] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timeout = window.setTimeout(() => setShowBrandIntro(false), prefersReducedMotion ? 700 : 2400);
    return () => window.clearTimeout(timeout);
  }, []);

  if (showContigoDetails) {
    return <CoverlyContigoScreen onBack={() => setShowContigoDetails(false)} />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F8F8FA] text-[#34313F]">
      {showBrandIntro && <BrandIntro />}
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pb-4 pt-7">
        <header className="relative flex h-12 items-center justify-center">
          <p className="text-[1.9rem] font-black tracking-tight text-[#5A4FC7]">
            Cover<span className="text-[#B8B2E7]">ly</span>
          </p>
          <button className="absolute right-0 flex h-10 w-10 items-center justify-center rounded-full text-[#23769B]" aria-label="Menu">
            <i className="ri-menu-line text-[1.65rem]" />
          </button>
        </header>

        <main className="flex flex-1 flex-col">
          <section className="flex flex-1 flex-col items-center justify-center pb-3 text-center">
            <p className="text-[2rem] font-black leading-none text-[#4A4852]">{greeting}</p>
            <p className="mt-7 text-[1.35rem] font-black uppercase tracking-wide text-[#66636D]">
              Cliente Coverly
            </p>

            <div className="mt-8 h-8" />

            <button
              onClick={onEntrar}
              className="mt-8 w-full rounded-full border border-[#2254C7] bg-white py-3.5 text-lg font-semibold text-[#23769B] transition-transform active:scale-[0.98]"
            >
              Ingresa con tu contrasena
            </button>

            <button
              onClick={onRegistrar}
              className="mt-3 w-full rounded-full bg-[#5A4FC7] py-3.5 text-base font-black text-white shadow-md shadow-[#5A4FC7]/18 transition-transform active:scale-[0.98]"
            >
              Registrate
            </button>

            <div className="mt-3 h-4" aria-hidden="true" />
          </section>

          <button
            type="button"
            onClick={() => setShowContigoDetails(true)}
            className="block w-full overflow-hidden rounded-[24px] border border-[#E3E1EA] bg-[#5A4FC7] text-left shadow-md shadow-[#5A4FC7]/12 transition-transform active:scale-[0.99]"
            aria-label="Conocer los beneficios de Coverly Contigo"
          >
            <img
              src="/coverly-contigo-login-banner.webp"
              alt="Coverly Contigo, beneficios para disfrutar en familia"
              className="block aspect-[2/1] w-full object-cover"
            />
          </button>

          <nav className="grid grid-cols-4 gap-2 pt-5">
            {quickLinks.map((item) => (
              <button
                key={item.label}
                onClick={item.label === 'Registrarme' ? onRegistrar : onEntrar}
                className="flex flex-col items-center gap-1.5 text-[#4A4852]"
              >
                <span className="flex h-8 w-8 items-center justify-center text-[#13234A]">
                  <i className={`${item.icon} text-[1.35rem]`} />
                </span>
                <span className="text-[11px] font-semibold leading-tight">{item.label}</span>
              </button>
            ))}
          </nav>
        </main>
      </div>
    </div>
  );
}

const contigoBenefits = [
  {
    icon: 'ri-play-circle-line',
    title: 'Meses de streaming',
    body: 'Recibe entretenimiento digital al cumplir las condiciones del programa o al afiliar nuevos socios.',
  },
  {
    icon: 'ri-user-shared-line',
    title: 'Membresia sin costo',
    body: 'Al afiliar 5 nuevos socios activos puedes obtener hasta 2 meses de membresia cubiertos por Coverly.',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Descuentos y convenios',
    body: 'Accede a beneficios disponibles en comercios, entretenimiento y servicios participantes.',
  },
  {
    icon: 'ri-gift-line',
    title: 'Reconocimientos especiales',
    body: 'Participa en beneficios de lealtad, aniversarios y otras experiencias para miembros activos.',
  },
];

function CoverlyContigoScreen({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen bg-[#F7F6FF] pb-10 text-[#34313F]">
      <header className="sticky top-0 z-20 bg-[#F7F6FF]/95 px-5 pb-3 pt-5 backdrop-blur">
        <div className="mx-auto flex w-full max-w-md items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#534AB7] shadow-sm"
            aria-label="Volver al inicio"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-wide text-[#716AA8]">Programa de beneficios</p>
            <h1 className="text-lg font-black text-[#211B4F]">Coverly Contigo</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-md px-5">
        <section className="overflow-hidden rounded-[26px] border border-[#E3E1EA] bg-white shadow-sm">
          <img
            src="/coverly-contigo-login-banner.webp"
            alt="Coverly Contigo, beneficios adicionales para miembros"
            className="aspect-[2/1] w-full object-cover"
          />
        </section>

        <section className="mt-5 rounded-[26px] bg-white p-5 shadow-sm">
          <p className="text-xs font-black uppercase tracking-wide text-[#534AB7]">Mas que una membresia</p>
          <h2 className="mt-2 text-xl font-black leading-tight text-[#211B4F]">Beneficios para acompañarte hoy</h2>
          <p className="mt-3 text-sm leading-6 text-[#5F5A6E]">
            Coverly Contigo reconoce a los miembros activos con beneficios de lealtad, experiencias y recompensas adicionales a los servicios de su plan.
          </p>
        </section>

        <section className="mt-4 overflow-hidden rounded-[26px] bg-[#211B4F] shadow-sm" aria-label="Video de Coverly Contigo pendiente de publicar">
          <div className="relative flex aspect-video items-center justify-center bg-[#211B4F]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(118,104,225,0.34),transparent_58%)]" />
            <div className="relative flex flex-col items-center text-center text-white">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/40 bg-white/10">
                <i className="ri-play-fill ml-0.5 text-2xl" />
              </span>
              <p className="mt-3 text-sm font-black">Video Coverly Contigo</p>
              <p className="mt-1 text-xs font-semibold text-white/65">Proximamente</p>
            </div>
          </div>
        </section>

        <section className="mt-5 grid gap-3">
          {contigoBenefits.map((benefit) => (
            <article key={benefit.title} className="flex gap-3 rounded-[22px] border border-[#E7E3F5] bg-white p-4 shadow-sm">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#EEEAFE] text-[#534AB7]">
                <i className={`${benefit.icon} text-lg`} />
              </span>
              <div>
                <h3 className="text-sm font-black text-[#211B4F]">{benefit.title}</h3>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#666074]">{benefit.body}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="mt-4 rounded-[22px] border border-[#D8D3F4] bg-[#F1EFFF] p-4">
          <p className="text-xs font-semibold leading-5 text-[#625C78]">
            Los beneficios estan sujetos a vigencia, disponibilidad y validacion de membresia activa. Consulta cada beneficio dentro de la app para conocer sus condiciones.
          </p>
        </section>
      </main>
    </div>
  );
}

function BrandIntro() {
  const squares = Array.from({ length: 18 });

  return (
    <div className="coverly-brand-intro fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-[#534AB7] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(255,255,255,0.12),transparent_38%),linear-gradient(155deg,#5F55CB_0%,#534AB7_58%,#463D9B_100%)]" />
      <div className="coverly-square-field absolute inset-0" aria-hidden="true">
        {squares.map((_, index) => (
          <span
            key={index}
            style={{
              '--x': `${(index * 37) % 100}%`,
              '--delay': `${(index % 7) * 0.075}s`,
              '--fall': `${38 + (index % 5) * 7}vh`,
              '--w': `${4 + (index % 3) * 2}px`,
              '--h': `${18 + (index % 4) * 10}px`,
            } as CSSProperties}
          />
        ))}
      </div>
      <div className="coverly-symbol-stage relative flex h-42 w-42 items-center justify-center sm:h-48 sm:w-48" aria-hidden="true">
        <img
          src="/coverly-isotipo-arc.png"
          alt=""
          className="coverly-symbol-arc absolute h-[8.5rem] w-[8.5rem] object-contain sm:h-[9.4rem] sm:w-[9.4rem]"
          draggable="false"
        />
        <img
          src="/coverly-isotipo-dot.png"
          alt=""
          className="coverly-symbol-dot absolute h-[2.15rem] w-[2.15rem] object-contain sm:h-[2.42rem] sm:w-[2.42rem]"
          draggable="false"
        />
        <img
          src="/coverly-isotipo.png"
          alt=""
          className="coverly-symbol-full absolute h-[8.7rem] w-[8.7rem] object-contain sm:h-[9.75rem] sm:w-[9.75rem]"
          draggable="false"
        />
        <span className="coverly-activation-pulse absolute rounded-full" />
      </div>
      <div className="coverly-wordmark-wrap relative mt-0 flex items-center justify-center" aria-label="Coverly">
        <img
          src="/coverly-wordmark.png"
          alt="Coverly"
          className="coverly-wordmark h-auto w-[12rem] sm:w-[13.75rem]"
          draggable="false"
        />
      </div>
    </div>
  );
}
